import React, { useState, useEffect } from 'react';
import { templateData } from '../../../data/data_templates';
import './AdministrationTemplates.css';
import UnifiedTemplateForm from '../../forms/UnifiedTemplateForm';
import CloneTemplateModal from './CloneTemplateModal';

function AdministrationTemplates() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [templateToDelete, setTemplateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showCloneModal, setShowCloneModal] = useState(false);
  const [templateToClone, setTemplateToClone] = useState(null);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateData.getTemplates();
      if (response && response.success) {
        // Ensure we have a valid array and filter out any invalid templates
        const validTemplates = (response.templates || []).filter(template => 
          template && template.template_id
        );
        setTemplates(validTemplates);
      } else {
        setError('Failed to fetch templates');
        setTemplates([]);
      }
    } catch (err) {
      console.error('Error fetching templates:', err);
      setError('Error loading templates');
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateNew = () => {
    setEditingTemplate(null);
    setShowForm(true);
  };

  const handleEditClick = (template) => {
    setEditingTemplate(template);
    setShowForm(true);
  };

  const handleDeleteClick = (template) => {
    setTemplateToDelete(template);
    setShowDeleteModal(true);
  };

  const handleCloneClick = (template) => {
    setTemplateToClone(template);
    setShowCloneModal(true);
  };



  const handleDeleteConfirm = async () => {
    if (!templateToDelete) return;

    try {
      setDeleting(true);
      const response = await templateData.deleteTemplate(templateToDelete.template_id);
      
      if (response && response.success) {
        // Remove the deleted template from the list
        setTemplates(templates.filter(t => t.template_id !== templateToDelete.template_id));
        setShowDeleteModal(false);
        setTemplateToDelete(null);
      } else {
        setError('Failed to delete template');
      }
    } catch (err) {
      console.error('Error deleting template:', err);
      setError('Error deleting template');
    } finally {
      setDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setShowDeleteModal(false);
    setTemplateToDelete(null);
  };

  const handleFormClose = () => {
    setShowForm(false);
    setEditingTemplate(null);
    setError(null);
    // Refresh templates after editing
    fetchTemplates();
  };

  const handleCloneSubmit = async (formData) => {
    if (!templateToClone || !templateToClone.template_id) {
      throw new Error('Invalid template to clone');
    }
    
    try {
      const response = await templateData.cloneTemplate(
        templateToClone.template_id,
        formData.name,
        formData.description
      );
      
      if (response && response.success) {
        // Check if the response has the expected structure
        if (response.template && response.template.template_id) {
          // Add the cloned template to the list
          setTemplates([...templates, response.template]);
        } else {
          // If the response doesn't have the expected structure, refresh the templates list
          await fetchTemplates();
        }
        setShowCloneModal(false);
        setTemplateToClone(null);
      } else {
        throw new Error('Failed to clone template');
      }
    } catch (err) {
      console.error('Error cloning template:', err);
      throw err;
    }
  };

  const handleCloneClose = () => {
    setShowCloneModal(false);
    setTemplateToClone(null);
  };

  const handleFormSubmit = async (formData, isNextStep = false) => {
    try {
      if (editingTemplate) {
        // Update existing template
        const response = await templateData.updateTemplate(
          editingTemplate.template_id,
          formData.type,
          formData.role,
          formData.name,
          formData.description,
          formData.active
        );
        
        if (response && response.success) {
          // Update the template in the list
          setTemplates(templates.map(t => 
            t.template_id === editingTemplate.template_id 
              ? { ...t, ...formData }
              : t
          ));
          // Only close form if this is the final submission (not from Next button)
          if (!isNextStep) {
            setShowForm(false);
            setEditingTemplate(null);
          }
        } else {
          setError('Failed to update template');
        }
      } else {
        // Create new template
        const response = await templateData.createTemplate(
          formData.type,
          formData.role,
          formData.name,
          formData.description
        );
        
        if (response && response.success) {
          // Add the new template to the list
          setTemplates([...templates, response.template]);
          
          // If this is a next step (not final submission), update the editingTemplate
          // so the form can access the template ID for objectives/tasks
          if (isNextStep) {
            setEditingTemplate(response.template);
          } else {
            // Only close form if this is the final submission
            setShowForm(false);
          }
        } else {
          setError('Failed to create template');
        }
      }
    } catch (err) {
      console.error('Error saving template:', err);
      setError('Error saving template');
    }
  };

  if (loading) {
    return (
      <div className="templates-container">
        <div className="templates-header">
          <h1>Templates</h1>
        </div>
        <div className="loading-state">
          <i className="ti ti-loader ti-spin"></i>
          <p>Loading templates...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="templates-container">
        <div className="templates-header">
          <h1>Templates</h1>
        </div>
        <div className="error-state">
          <i className="ti ti-alert-circle"></i>
          <p>{error}</p>
          <button onClick={fetchTemplates} className="retry-button">
            <i className="ti ti-refresh"></i>
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="templates-container">
      <div className="templates-header">
        <h1>Templates</h1>
        <p>Manage interview and meeting templates</p>
      </div>
      
      <div className="templates-actions">
        <button onClick={handleCreateNew} className="create-button">
          <i className="ti ti-plus"></i>
          Create New Template
        </button>
      </div>
      
      <div className="templates-table-container">
        <table className="templates-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Name</th>
              <th>Type</th>
              <th>Role</th>
              <th>Description</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {templates.filter(template => template && template.template_id).map((template) => (
              <tr key={template.template_id}>
                <td className="template-id">{template.template_id}</td>
                <td className="template-name" title={template.name}>{template.name}</td>
                <td className="template-type">
                  <span className="type-badge">{template.type}</span>
                </td>
                <td className="template-role" title={template.role}>{template.role}</td>
                <td className="template-description" title={template.description}>{template.description}</td>
                <td className="template-status">
                  <span className={`status-badge ${template.active ? 'active' : 'inactive'}`}>
                    {template.active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="template-actions">
                  <button 
                    onClick={() => handleEditClick(template)} 
                    className="edit-button"
                    title="Edit Template"
                  >
                    <i className="ti ti-edit"></i>
                  </button>
                  <button 
                    onClick={() => handleCloneClick(template)} 
                    className="clone-button"
                    title="Clone Template"
                  >
                    <i className="ti ti-files"></i>
                  </button>
                  <button 
                    onClick={() => handleDeleteClick(template)} 
                    className="delete-button"
                    title="Delete Template"
                  >
                    <i className="ti ti-trash"></i>
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {templates.length === 0 && (
          <div className="empty-state">
            <i className="ti ti-file-text"></i>
            <p>No templates found</p>
          </div>
        )}
      </div>

      {/* Unified Template Form Modal */}
      {showForm && (
        <UnifiedTemplateForm
          template={editingTemplate}
          onSubmit={handleFormSubmit}
          onClose={handleFormClose}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteModal && (
        <div className="modal-overlay">
          <div className="delete-confirmation-modal">
            <div className="modal-header">
              <h3>Delete Template</h3>
            </div>
            <div className="modal-content">
              <p>
                Are you sure you want to delete the template "<strong>{templateToDelete?.name}</strong>"?
              </p>
              <p className="warning-text">
                This action cannot be undone. All objectives, tasks, focuses, and conclusions associated with this template will also be removed.
              </p>
            </div>
            <div className="modal-actions">
              <button 
                onClick={handleDeleteCancel} 
                className="cancel-button"
                disabled={deleting}
              >
                Cancel
              </button>
              <button 
                onClick={handleDeleteConfirm} 
                className="delete-confirm-button"
                disabled={deleting}
              >
                {deleting ? (
                  <>
                    <i className="ti ti-loader ti-spin"></i>
                    Deleting...
                  </>
                ) : (
                  <>
                    <i className="ti ti-trash"></i>
                    Delete
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Clone Template Modal */}
      {showCloneModal && (
        <CloneTemplateModal
          template={templateToClone}
          onSubmit={handleCloneSubmit}
          onClose={handleCloneClose}
        />
      )}
    </div>
  );
}

export default AdministrationTemplates; 